import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { usePostHog } from 'posthog-js/react'

import { setDesiredEarMode } from "../store/audioSettingsSlice";
import { RootState } from "../store/store";
import { selectPttHidden } from '../store/flagsSlice';
import { selectSessionId } from '../store/sessionSlice';
import { WebSocketConnection, ConnectionStatus } from "../utils/WebSocketConnection";
import { AudioMode } from "../utils/audioUtils";
import {
  AudioMessage_EarMode as EarMode,
  AudioMessage_ServerInfo_HealthStatus as HealthStatus,
} from "../generated/audio_message";
import { KeyType } from "../utils/MicrophoneImpl";

import { useMicEngine } from "../components/useroverlay/useMicEngine";
import { useSpeechDetection } from "../components/useroverlay/useSpeechDetection";
import { useMicDisplayState } from "../components/useroverlay/useMicDisplayState";
import { useHasKeyboard } from "./useHasKeyboard";
import { getGUIThemeProperties } from "../utils/baseConfig";
import { selectGUITheme } from "../store/userSettingsSlice";

export interface UseMicrophoneProps {
  onMicrophoneActivated?: () => void;
  onMicrophoneDeactivating?: () => void;
  audioMode: AudioMode;
  connection: WebSocketConnection | null;
}

export interface UseMicrophoneReturn {
  // State
  isWindowFocused: boolean;
  socketConnected: boolean;
  healthStatus: HealthStatus;
  active: boolean;
  isOpenMic: boolean;
  pttHidden: boolean;
  hasEverTransmitted: boolean;
  isTalkingButNotTransmitting: boolean;
  showPermissionOverlay: boolean;
  displayState: ReturnType<typeof useMicDisplayState>;

  // GUI Style properties
  guiTheme: string;
  isTouchDevice: boolean;
  micStaticText: string | null;
  micColor: string | null;
  mobileLayoutClass: string;
  showLiveMicToggle: boolean;

  // Event handlers
  handleTouchStart: (e: React.TouchEvent) => Promise<void>;
  handleTouchEnd: (e: React.TouchEvent) => void;
  handleMicToggleChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;

  // Functions
  activateMicrophone: (keyType: KeyType) => Promise<void>;
  deactivateMicrophone: () => void;
}

export function useMicrophone({
  onMicrophoneActivated,
  onMicrophoneDeactivating,
  audioMode,
  connection,
}: UseMicrophoneProps): UseMicrophoneReturn {
  const [isWindowFocused, setIsWindowFocused] = useState(true);
  const [socketConnected, setSocketConnected] = useState(true);
  const [pageLoadTime] = useState(() => Date.now());
  const [healthStatus, setHealthStatus] = useState<HealthStatus>(HealthStatus.NO_DATA);

  const location = useLocation();
  const dispatch = useDispatch();
  const posthog = usePostHog();

  // useMicEngine hides mic permission, activation/deactivation
  const {
    mic,
    active,
    midDeactivation,
    isInitializing,
    needsPermission,
    activateMicrophone,
    deactivateMicrophone,
    hasEverTransmitted,
    showPermissionOverlay,
    resetHasEverTransmitted,
  } = useMicEngine({
    healthStatus,
    onActivated: onMicrophoneActivated,
    onDeactivatingLogic: onMicrophoneDeactivating,
  });

  // useSpeechDetection provides flags about whether the user is talking
  const {
    isTalkingButNotTransmitting,
    isSilentWhileActive,
    isResumedTalking,
    isStillWithUs,
  } = useSpeechDetection(mic, active, hasEverTransmitted);

  // Redux state
  const isOpenMic = useSelector(
    (state: RootState) => state.audioSettings.desiredEarMode === EarMode.CONVERSATION
  );
  const pttHidden = useSelector(selectPttHidden);
  const guiTheme = useSelector(selectGUITheme);
  const sessionId = useSelector(selectSessionId);

  // Detect touch devices
  const hasKeyboard = useHasKeyboard();
  const isTouchDevice = !hasKeyboard;

  // Get microphone style properties
  const styleProps = getGUIThemeProperties(guiTheme);
  const micStaticText = isTouchDevice ? styleProps.micStaticText : null;
  const micVolatile = styleProps.micVolatile;
  const micColor = null;
  const mobileLayoutClass = isTouchDevice ? `mobile-layout-${guiTheme}` : '';
  const showLiveMicToggle = audioMode === "open-mic" || styleProps.allowLiveMic;

  // useMicDisplayState determines what to show on the mic button
  const displayState = useMicDisplayState({
    socketConnected,
    pageLoadTime,
    healthStatus,
    active,
    midDeactivation,
    isInitializing,
    needsPermission,
    hasEverTransmitted,
    isSilentWhileActive,
    isStillWithUs,
    isResumedTalking,
    isTalkingButNotTransmitting,
    isWindowFocused,
    isOpenMic,
    showLiveMicToggle,
    micStaticText: micStaticText || undefined,
    micVolatile: micVolatile,
  });

  // Set up connection status and health status listeners
  useEffect(() => {
    const connectionStatusCallback = (status: ConnectionStatus) => {
      console.log("[useMicrophone] Connection status changed:", status);
      setSocketConnected(status === "connected");
    };

    const healthStatusCallback = (status: HealthStatus) => {
      setHealthStatus(status);
    };

    const conn = connection;
    conn?.addStatusListener("mic", connectionStatusCallback);
    conn?.addHealthStatusListener("mic", healthStatusCallback);
    return () => {
      conn?.removeStatusListener("mic");
      conn?.removeHealthStatusListener("mic");
    };
  }, [connection]);

  // Setup window focus/blur listeners
  useEffect(() => {
    const handleFocus = () => setIsWindowFocused(true);
    const handleBlur = () => setIsWindowFocused(false);

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, [location.search]);

  // Setup keyboard event handlers for push-to-talk
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (isOpenMic) return;

      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA");

      if (!e.repeat && !isInputFocused && e.code === "Space") {
        e.preventDefault();
        posthog?.capture("ptt_spacebar", { sessionId, start: true });
        await activateMicrophone(KeyType.SPACE);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (isOpenMic) return;

      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA");

      if (!isInputFocused && e.code === "Space") {
        e.preventDefault();
        posthog?.capture("ptt_spacebar", { sessionId, start: false });
        deactivateMicrophone();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [activateMicrophone, deactivateMicrophone, isOpenMic, sessionId]);

  // Touch event handlers for mobile devices
  const handleTouchStart = useCallback(async (e: React.TouchEvent) => {
    if (isOpenMic) return;
    e.preventDefault();
    await activateMicrophone(KeyType.SPACE);
  }, [activateMicrophone]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (isOpenMic) return;
    e.preventDefault();
    deactivateMicrophone();
  }, [deactivateMicrophone]);

  // Handler for the microphone toggle switch
  const handleMicToggleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const newState = e.target.checked;

      const earMode = newState ? EarMode.CONVERSATION : EarMode.PUSH_TO_TALK;
      dispatch(setDesiredEarMode(earMode));

      if (newState) {
        await activateMicrophone(KeyType.SPACE);
      } else {
        deactivateMicrophone();
      }

      console.log(
        `[useMicrophone] toggle switched to: ${newState ? "ON" : "OFF"
        }, Ear Mode: ${earMode}, Microphone ${newState ? "activated" : "deactivated"}`
      );
    },
    [dispatch, activateMicrophone, deactivateMicrophone]
  );

  // React to external changes in desiredEarMode
  const prevIsOpenMicRef = useRef<boolean>(isOpenMic);
  useEffect(() => {
    if (prevIsOpenMicRef.current !== isOpenMic) {
      console.log(
        `[useMicrophone] desiredEarMode changed externally to: ${isOpenMic ? "CONVERSATION" : "PUSH_TO_TALK"
        }`
      );

      if (isOpenMic) {
        activateMicrophone(KeyType.SPACE);
      } else {
        // Switching to push-to-talk: deactivate and reset the transmitted flag
        deactivateMicrophone();
        resetHasEverTransmitted();
      }
    }

    prevIsOpenMicRef.current = isOpenMic;
  }, [isOpenMic, activateMicrophone, deactivateMicrophone, resetHasEverTransmitted]);

  return {
    // State
    isWindowFocused,
    socketConnected,
    healthStatus,
    active,
    isOpenMic,
    pttHidden,
    hasEverTransmitted,
    isTalkingButNotTransmitting,
    showPermissionOverlay,
    displayState,

    // GUI Style properties
    guiTheme,
    isTouchDevice,
    micStaticText,
    micColor,
    mobileLayoutClass,
    showLiveMicToggle,

    // Event handlers
    handleTouchStart,
    handleTouchEnd,
    handleMicToggleChange,

    // Functions
    activateMicrophone,
    deactivateMicrophone,
  };
}
